import { useState, useEffect } from 'react';
import { useGetDomains, useCreateDomain } from '../../../hooks/useDomains';
import { useDomainContext } from '../../../contexts/DomainContext';
import { DiscoverDomainsModal } from './DiscoverDomainsModal';

export function DomainSelector() {
    const { data: domains, isLoading, isError } = useGetDomains();
    const createDomainMutation = useCreateDomain();
    const { activeDomainId, setActiveDomainId } = useDomainContext();
    const [isCreating, setIsCreating] = useState(false);
    const [isDiscovering, setIsDiscovering] = useState(false);
    const [newDomainName, setNewDomainName] = useState('');

    // Auto-select first domain if none is selected
    useEffect(() => {
        if (!isLoading && domains && domains.length > 0 && !activeDomainId) {
            setActiveDomainId(domains[0].id);
        }
    }, [domains, isLoading, activeDomainId, setActiveDomainId]);

    const handleCreateDomain = async () => {
        if (!newDomainName.trim()) return;
        try {
            const result = await createDomainMutation.mutateAsync(newDomainName.trim());
            setActiveDomainId(result.id);
            setNewDomainName('');
            setIsCreating(false);
        } catch (error) {
            console.error('Failed to create domain:', error);
            alert('Failed to create domain. Please try again.');
        }
    };

    if (isLoading) {
        return <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Loading domains...</div>;
    }

    if (isError) {
        return <div style={{ color: 'var(--accent-danger)', fontSize: '0.85rem' }}>Error loading domains</div>;
    }

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>Domain:</span>
            {isCreating ? (
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                        type="text"
                        value={newDomainName}
                        onChange={(e) => setNewDomainName(e.target.value)}
                        placeholder="Domain name"
                        style={{
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            border: '1px solid var(--surface-glass-border)',
                            background: 'var(--bg-app)',
                            color: 'var(--text-primary)',
                        }}
                        autoFocus
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleCreateDomain();
                            if (e.key === 'Escape') setIsCreating(false);
                        }}
                    />
                    <button
                        onClick={handleCreateDomain}
                        disabled={createDomainMutation.isPending}
                        style={{ padding: '0.25rem 0.5rem', cursor: 'pointer', borderRadius: '4px', border: '1px solid var(--surface-glass-border)', background: 'var(--accent-primary)', color: 'var(--text-primary)' }}
                    >
                        {createDomainMutation.isPending ? '...' : 'Save'}
                    </button>
                    <button
                        onClick={() => setIsCreating(false)}
                        style={{ padding: '0.25rem 0.5rem', cursor: 'pointer', borderRadius: '4px', border: '1px solid var(--surface-glass-border)', background: 'transparent', color: 'var(--text-primary)' }}
                    >
                        Cancel
                    </button>
                </div>
            ) : (
                <>
                    <select
                        value={activeDomainId || ''}
                        onChange={(e) => setActiveDomainId(e.target.value)}
                        style={{
                            padding: '0.4rem 0.8rem',
                            borderRadius: '6px',
                            border: '1px solid var(--surface-glass-border)',
                            background: 'var(--bg-card)',
                            color: 'var(--text-primary)',
                            cursor: 'pointer',
                        }}
                    >
                        <option value="" disabled>Select Domain</option>
                        {domains?.map((d) => (
                            <option key={d.id} value={d.id}>{d.name}</option>
                        ))}
                    </select>
                    <button
                        onClick={() => setIsCreating(true)}
                        style={{
                            padding: '0.4rem',
                            borderRadius: '6px',
                            border: '1px solid var(--surface-glass-border)',
                            background: 'transparent',
                            color: 'var(--text-primary)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                        title="Create new domain"
                    >
                        ➕
                    </button>
                    <button
                        onClick={() => setIsDiscovering(true)}
                        style={{
                            padding: '0.4rem',
                            borderRadius: '6px',
                            border: '1px solid var(--surface-glass-border)',
                            background: 'transparent',
                            color: 'var(--text-primary)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                        title="Discover shared desks"
                    >
                        🌐
                    </button>
                    {isDiscovering && <DiscoverDomainsModal onClose={() => setIsDiscovering(false)} />}
                </>
            )}
        </div>
    );
}
